import React, { useState, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, Button, Spinner, Form, Modal } from "react-bootstrap";
import CommonSnackbar, { CircularLoader } from "../common/reusable-commponent";
import CommonDropdown from "../common/common_dropDown";
import deleteIcon from "../../assets/svg/delete-po.svg";
import { submitStopsAPI, updateStopsAPI, getStopsEditApi, getCustomerAddressApi } from "./stops-api";
import SearchWithFreeText from "../common/common_auto_freesuggestion";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../common/skeleton-effect.css";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";
import "./stops-create.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const LocationPicker = ({ setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });
  return null;
};

const INITIAL_ERROR_STATE = {
  customer_id: "",
  location_id: "",
  from_address: "",
  from_country_id: "",
  from_state_id: "",
  from_city_id: "",
  from_zipcode: "",
  from_latitude: "",
  from_longitude: "",
  to_address: "",
  to_country_id: "",
  to_state_id: "",
  to_city_id: "",
  to_zipcode: "",
  to_latitude: "",
  to_longitude: "",
};

export const StopCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  const [isSearching, setIsSearching] = useState(false);
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`);
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setMapCenter([lat, lng]);
    setMapPosition({ lat, lng });
    setSearchResults([]);
    setSearchQuery(result.display_name);
  };
  useEffect(() => {
    if (id != null) {
      getStopEdit(id);
    }
  }, [id]);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Map state
  const [showMap, setShowMap] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [activeField, setActiveField] = useState(null);

  const [isLoader, setIsLoader] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [customerOption, setCustomerOption] = useState(null);
  const [locationOption, setLocationOption] = useState(null);
  const [fromAddress, setFromAddress] = useState("");
  const [fromAddressOption, setFromAddressOption] = useState(null);
  const [fromCountryOption, setFromCountryOption] = useState(null);
  const [fromStateOption, setFromStateOption] = useState(null);
  const [fromCityOption, setFromCityOption] = useState(null);
  const [fromZipCode, setFromZipCode] = useState("");
  const [fromLatitude, setFromLatitude] = useState("");
  const [fromLongitude, setFromLongitude] = useState("");

  const [toAddress, setToAddress] = useState("");
  const [toAddressOption, setToAddressOption] = useState(null);
  const [toCountryOption, setToCountryOption] = useState(null);
  const [toStateOption, setToStateOption] = useState(null);
  const [toCityOption, setToCityOption] = useState(null);
  const [toZipCode, setToZipCode] = useState("");
  const [toLatitude, setToLatitude] = useState("");
  const [toLongitude, setToLongitude] = useState("");

  const handleMapConfirm = () => {
    if (mapPosition) {
      const lat = mapPosition.lat.toFixed(6);
      const lng = mapPosition.lng.toFixed(6);

      if (activeField === "addr1") {
        setFromLatitude(lat);
        setFromLongitude(lng);
      } else if (activeField === "addr2") {
        setToLatitude(lat);
        setToLongitude(lng);
      }

      setShowMap(false);
      setMapPosition(null);
    }
  };

  const [errorMsg, setErrorMsg] = useState(INITIAL_ERROR_STATE);



  const [productList, setProductList] = useState([]);
  const [groupedError, setGroupedError] = useState({});
  const [removedProductList, setRemovedProductList] = useState([]);

  const getErrorMsg = (index, field) => {
    const error = groupedError[index]?.[field];
    if (error && Array.isArray(error)) {
      const fieldDisp = field === "product_id" ? "product" : field;
      return error[0]?.replace(`products.${index}.${field}`, fieldDisp) ?? "";
    }
    return "";
  };

  const submitForm = async () => {
    setIsLoader(true);
    setErrorMsg(INITIAL_ERROR_STATE);
    setGroupedError({});

    try {
      const updatedData = productList
        .filter((item) => item.product !== null)
        .map((item) => ({
          id: item.id ?? 0,
          product_id: item.product?.value ?? "",
          unit: item.unit?.label ?? item.unit?.value ?? "",
          quantity: item.quantity ?? "",
          weight: item.weight ?? "",
          volume: item.volume ?? "",
          price: item.price ?? "",
        }));

      const payloadData = {
        customer_id: customerOption?.value ?? "",
        location_id: locationOption?.value ?? "",
        from_address: fromAddress ?? "",
        from_country_id: fromCountryOption?.value ?? "",
        from_state_id: fromStateOption?.value ?? "",
        from_city_id: fromCityOption?.value ?? "",
        from_zipcode: fromZipCode ?? "",
        from_latitude: fromLatitude ?? "",
        from_longitude: fromLongitude ?? "",
        to_address: toAddress ?? "",
        to_country_id: toCountryOption?.value ?? "",
        to_state_id: toStateOption?.value ?? "",
        to_city_id: toCityOption?.value ?? "",
        to_zipcode: toZipCode ?? "",
        to_latitude: toLatitude ?? "",
        to_longitude: toLongitude ?? "",
        products: updatedData,
        removed_products: removedProductList,
      };

      const response = id == null
        ? await submitStopsAPI(payloadData)
        : await updateStopsAPI(id, payloadData);

      if (response?.data?.success === true) {
        setSnackbarMessage(response.data.message || "Success");
        setSnackbarStatus(true);
        handleSnackbarShow();
        setTimeout(() => navigate(-1), 1500);
      } else {
        const errors = response?.data?.error || {};
        const validationTemp = { ...INITIAL_ERROR_STATE };
        const groupedErrorTemp = {};

        Object.entries(errors).forEach(([key, value]) => {
          const match = key.match(/^products\.(\d+)\.(.+)$/);
          if (match) {
            const index = match[1];
            const field = match[2];
            if (!groupedErrorTemp[index]) groupedErrorTemp[index] = {};
            groupedErrorTemp[index][field] = value;
          } else if (key in validationTemp) {
            validationTemp[key] = value[0] || "";
          }
        });

        setErrorMsg(validationTemp);
        setGroupedError(groupedErrorTemp);

        if (response?.data?.message) {
          setSnackbarMessage(response.data.message);
          setSnackbarStatus(false);
          handleSnackbarShow();
        }
      }
    } catch (err) {
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsLoader(false);
    }
  };

  const getStopEdit = async (id) => {
    setIsEditLoading(true);
    try {
      const response = await getStopsEditApi(id);
      if (response?.data?.success === true) {
        const stopDetail = response.data.stops ?? {};

        setCustomerOption({
          value: stopDetail.customer_id ?? "",
          label: stopDetail.customer_code ?? "",
        });
        setLocationOption({
          value: stopDetail.location_id ?? "",
          label: stopDetail.location ?? "",
        });
        setFromAddress(stopDetail.from_address ?? "");
        setFromAddressOption({
          value: stopDetail.from_address ?? "",
          label: stopDetail.from_address ?? "",
        });
        setFromCountryOption({
          value: stopDetail.from_country_id ?? "",
          label: stopDetail.from_country ?? "",
        });
        setFromStateOption({
          value: stopDetail.from_state_id ?? "",
          label: stopDetail.from_state ?? "",
        });
        setFromCityOption({
          value: stopDetail.from_city_id ?? "",
          label: stopDetail.from_city ?? "",
        });
        setFromZipCode(stopDetail.from_zipcode ?? "");
        setFromLatitude(stopDetail.from_latitude ?? "0.0");
        setFromLongitude(stopDetail.from_longitude ?? "0.0");

        setToAddress(stopDetail.to_address ?? "");
        setToAddressOption({
          value: stopDetail.to_address ?? "",
          label: stopDetail.to_address ?? "",
        });
        setToCountryOption({
          value: stopDetail.to_country_id ?? "",
          label: stopDetail.to_country ?? "",
        });
        setToStateOption({
          value: stopDetail.to_state_id ?? "",
          label: stopDetail.to_state ?? "",
        });
        setToCityOption({
          value: stopDetail.to_city_id ?? "",
          label: stopDetail.to_city ?? "",
        });
        setToZipCode(stopDetail.to_zipcode ?? "");
        setToLatitude(stopDetail.to_latitude ?? "0.0");
        setToLongitude(stopDetail.to_longitude ?? "0.0");

        const productTemp = (stopDetail.product || []).map(item => ({
          id: item.id,
          product: { value: item.item_id, label: item.item },
          weight: item.weight,
          volume: item.voulme,
          unit: { value: item.unit, label: item.unit },
          quantity: item.quantity,
          price: item.price,
        }));
        setProductList(productTemp);
      }
    } catch (err) {
      // Error handled by apiCall
    } finally {
      setIsEditLoading(false);
    }
  };
  const getCustomerAddress = async (id) => {
    try {
      const response = await getCustomerAddressApi(id);

      if (response.data.success === true) {
        const cusAddressDetail = response.data.customeradreess ?? {};

        setToAddress(cusAddressDetail.address ?? "");
        setToAddressOption({
          value: cusAddressDetail.address ?? "",
          label: cusAddressDetail.address ?? "",
        });
        setToCountryOption({
          value: cusAddressDetail.country_id ?? "",
          label: cusAddressDetail.country ?? "",
        });
        setToStateOption({
          value: cusAddressDetail.state_id ?? "",
          label: cusAddressDetail.state ?? "",
        });
        setToCityOption({
          value: cusAddressDetail.city_id ?? "",
          label: cusAddressDetail.city ?? "",
        });
        setToZipCode(cusAddressDetail.zipcode ?? "");
        setToLatitude(cusAddressDetail.latitude ?? "0.0");
        setToLongitude(cusAddressDetail.longitude ?? "0.0");
      } else {
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };
  return (
    <div>
      <div className="scroll-hidden stops-create-container">
        <div className="px-2 d-flex align-items-center justify-content-between">
          <div className="d-flex gap-3 my-3">
            <MoveLeft
              className="cursor-pointer"
              onClick={() => {
                navigate(-1);
              }}
            />
            <p className="fw-semibold m-0">New stops</p>
          </div>
          <div className="d-flex gap-3">
            <Button
              className="cursor-pointer rounded-pill px-4 py-2 cancel-btn fs-15 fw-500"
              onClick={() => {
                navigate(-1);
              }}
            >
              Cancel
            </Button>
            <Button
              className={`cursor-pointer dark-purple-btn save-btn ${isLoader ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
              onClick={() => {
                submitForm();
              }}
            >
              {isLoader ? <Spinner animation="border" size="sm" /> : id != null ? "Update" : "Save"}
            </Button>
          </div>
        </div>
        <div className="col-12 px-2">
          <div className="row mb-2 d-flex align-items-stretch">
            <div className="col-12 col-lg-4 pe-0">
              <div className="h-100 create-section-card d-flex flex-column align-items-start px-2">
                <div className="section-title">
                  STOP DETAILS
                </div>

                {isEditLoading ? (
                  <>
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <div key={idx} className="mt-2 col-12 d-flex flex-column align-items-start">
                        <div className="skeleton skeleton-text mb-2" style={{ width: "80px", height: "12px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="mt-2 col-12 d-flex flex-column align-items-start">
                      <div className="form-label-custom">
                        Customer <span className="error-text">*</span>
                      </div>
                      <div className="w-100">
                        <CommonDropdown
                          apiUrl={`${apiUrl}customer/autosuggestion`}
                          placeholder="Select"
                          value={customerOption}
                          onSelect={(item) => {
                            setCustomerOption(item);
                            getCustomerAddress(item.value);
                          }}
                        />
                      </div>
                      {errorMsg.customer_id && <span className="error-message">{errorMsg.customer_id}</span>}
                    </div>
                    <div className="mt-2 col-12 d-flex flex-column align-items-start">
                      <div className="form-label-custom">
                        Customer ID
                      </div>
                      <div className="w-100 rounded-3 px-2 d-flex align-items-center justify-content-start read-only-field">
                        <span className="fs-14 fw-300 " style={{ color: "#8790A1" }}>
                          {customerOption?.customer_code ?? ""}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 col-12 d-flex flex-column align-items-start">
                      <div className="form-label-custom">
                        Location <span className="error-text">*</span>
                      </div>
                      <div className="w-100">
                        <CommonDropdown
                          apiUrl={`${apiUrl}get_locations`}
                          placeholder="Select"
                          value={locationOption}
                          onSelect={(item) => {
                            setLocationOption(item);
                          }}
                        />
                      </div>
                      {errorMsg.location_id && <span className="error-message">{errorMsg.location_id}</span>}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="col-12 col-lg-4 pe-0">
              <div className="create-section-card d-flex flex-column align-items-start px-2">
                <div className="section-title">
                  FROM ADDRESS
                </div>

                {isEditLoading ? (
                  <>
                    {/* Address Field Skeleton */}
                    <div className="mt-2 col-12 d-flex flex-column align-items-start">
                      <div className="skeleton skeleton-text mb-2" style={{ width: "80px", height: "12px" }} />
                      <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                    </div>

                    {/* City and State Row Skeleton */}
                    <div className="mt-2 col-12">
                      <div className="row">
                        <div className="col-12 col-lg-6">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "60px", height: "12px" }} />
                          <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                        </div>
                        <div className="col-12 col-lg-6 ps-0 ps-lg-2">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "60px", height: "12px" }} />
                          <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                        </div>
                      </div>
                    </div>

                    {/* Country and Zip Code Row Skeleton */}
                    <div className="mt-2 col-12">
                      <div className="row">
                        <div className="col-12 col-lg-6">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "70px", height: "12px" }} />
                          <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                        </div>
                        <div className="col-12 col-lg-6 ps-0 ps-lg-2">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "70px", height: "12px" }} />
                          <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                        </div>
                      </div>
                    </div>

                    {/* Latitude, Longitude, Button Row Skeleton */}
                    <div className="mt-2 col-12 mb-3">
                      <div className="row px-2">
                        <div className="col-12 col-md-4 px-1">
                          <div className="skeleton" style={{ width: "100%", height: "38px", borderRadius: "8px" }} />
                        </div>
                        <div className="col-12 col-md-4 px-1">
                          <div className="skeleton" style={{ width: "100%", height: "38px", borderRadius: "8px" }} />
                        </div>
                        <div className="col-12 col-md-4 px-1">
                          <div className="skeleton" style={{ width: "100%", height: "38px", borderRadius: "8px" }} />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-2 col-12 d-flex flex-column align-items-start">
                      <div className="form-label-custom">
                        Address <span className="error-text">*</span>
                      </div>
                      <div className="col-12">
                        <SearchWithFreeText
                          apiUrl={`${apiUrl}stops/from_address`}
                          placeholder="Search & type..."
                          selectedValue={fromAddressOption}
                          onDropdownChange={(item) => {
                            setFromAddressOption(item);
                            setFromAddress(item.from_address);
                            setFromCountryOption({
                              value: item.from_country_id ?? "",
                              label: item.from_country ?? "",
                            });
                            setFromStateOption({
                              value: item.from_state_id ?? "",
                              label: item.from_state ?? "",
                            });
                            setFromCityOption({
                              value: item.from_city_id ?? "",
                              label: item.from_city ?? "",
                            });
                            setFromZipCode(item.from_zipcode ?? "");
                            setFromLatitude(item.latitude ?? "");
                            setFromLongitude(item.longitude ?? "");
                          }}
                          onChangeSearch={(value) => {
                            console.log("Search input changed:", value);
                            setFromAddress(value);
                            setFromAddressOption({ label: value, value: value });
                          }}
                        />
                      </div>
                      {errorMsg.from_address && <span className="error-message">{errorMsg.from_address}</span>}
                    </div>

                    <div className="col-12">
                      <div className="row">
                        <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start">
                          <div className="form-label-custom">
                            City <span className="error-text">*</span>
                          </div>
                          <div className="w-100">
                            <CommonDropdown
                              apiUrl={`${adminUrl}get_city`}
                              placeholder="Select"
                              value={fromCityOption}
                              onSelect={(item) => {
                                setFromCityOption(item);
                                setFromStateOption({
                                  value: item.state_id,
                                  label: item.state_name,
                                });
                                setFromCountryOption({
                                  value: item.country_id,
                                  label: item.country_name,
                                });
                              }}
                            />
                          </div>
                          {errorMsg.from_city_id && <span className="error-message">{errorMsg.from_city_id}</span>}
                        </div>

                        <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start  ps-0">
                          <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                            State <span className="error-text">*</span>
                          </div>
                          <div className="w-100">
                            <CommonDropdown
                              apiUrl={`${adminUrl}get_state`}
                              placeholder="Select"
                              value={fromStateOption}
                              onSelect={(item) => {
                                setFromStateOption(item);
                                setFromCountryOption({
                                  value: item.country_id,
                                  label: item.country_name,
                                });
                              }}
                            />
                          </div>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "start",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FF4444",
                              marginTop: "6px",
                              marginLeft: "6px",
                            }}
                          >
                            {errorMsg.from_country_id}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="row">
                        <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start">
                          <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                            Country <span className="error-text">*</span>
                          </div>
                          <div className="w-100">
                            <CommonDropdown
                              apiUrl={`${adminUrl}get_country`}
                              placeholder="Select"
                              value={fromCountryOption}
                              onSelect={(item) => {
                                setFromCountryOption(item);
                              }}
                            />
                          </div>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "start",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FF4444",
                              marginTop: "6px",
                              marginLeft: "6px",
                            }}
                          >
                            {errorMsg.from_country_id}
                          </span>
                        </div>
                        <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start ps-0">
                          <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                            Zip code <span className="error-text">*</span>
                          </div>
                          <div className="w-100">
                            <input
                              type="text"
                              className="form-control rounded-3 "
                              id="zipcode"
                              placeholder="Enter here"
                              value={fromZipCode}
                              onChange={(e) => {
                                setFromZipCode(e.target.value);
                              }}
                            />
                          </div>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "start",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FF4444",
                              marginTop: "6px",
                              marginLeft: "6px",
                            }}
                          >
                            {errorMsg.from_zipcode}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 mb-3">
                      <div className="row px-2">
                        <div className="mt-2 col-12 col-md-4 px-1 ">
                          <input type="text" placeholder="Latitude" className="form-control rounded-3 fs-13" value={fromLatitude} readOnly />
                          {errorMsg.from_latitude && (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "start",
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#FF4444",
                                marginTop: "6px",
                                marginLeft: "6px",
                              }}
                            >
                              {errorMsg.from_latitude}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 col-12 col-md-4 px-1 ">
                          <input type="text" placeholder="Longitude" className="form-control rounded-3 fs-13" value={fromLongitude} readOnly />
                          {errorMsg.from_longitude && (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "start",
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#FF4444",
                                marginTop: "6px",
                                marginLeft: "6px",
                              }}
                            >
                              {errorMsg.from_longitude}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 col-12 col-md-4  px-1">
                          <Button
                            variant="primary"
                            className="w-100 fs-13 fw-500 d-flex align-items-center justify-content-center gap-1"
                            style={{
                              backgroundColor: "#3469E7",
                              borderColor: "#3469E7",
                            }}
                            onClick={() => {
                              setSearchQuery("");
                              setActiveField("addr1");
                              setMapPosition(null);
                              setShowMap(true);
                            }}
                          >
                            Pin in map
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="col-12 col-lg-4 pe-0">
              <div className="bg-white rounded-4 d-flex flex-column align-items-start px-2">
                <div className="mt-2 fs-14 fw-700" style={{ color: "#28303F" }}>
                  TO ADDRESS
                </div>

                {isEditLoading ? (
                  <>
                    {/* Address Field Skeleton */}
                    <div className="mt-2 col-12 d-flex flex-column align-items-start">
                      <div className="skeleton skeleton-text mb-2" style={{ width: "80px", height: "12px" }} />
                      <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                    </div>

                    {/* City and State Row Skeleton */}
                    <div className="mt-2 col-12">
                      <div className="row">
                        <div className="col-12 col-lg-6">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "60px", height: "12px" }} />
                          <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                        </div>
                        <div className="col-12 col-lg-6 ps-0 ps-lg-2">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "60px", height: "12px" }} />
                          <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                        </div>
                      </div>
                    </div>

                    {/* Country and Zip Code Row Skeleton */}
                    <div className="mt-2 col-12">
                      <div className="row">
                        <div className="col-12 col-lg-6">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "70px", height: "12px" }} />
                          <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                        </div>
                        <div className="col-12 col-lg-6 ps-0 ps-lg-2">
                          <div className="skeleton skeleton-text mb-2" style={{ width: "70px", height: "12px" }} />
                          <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                        </div>
                      </div>
                    </div>

                    {/* Latitude, Longitude, Button Row Skeleton */}
                    <div className="mt-2 col-12 mb-3">
                      <div className="row px-2">
                        <div className="col-12 col-md-4 px-1">
                          <div className="skeleton" style={{ width: "100%", height: "38px", borderRadius: "8px" }} />
                        </div>
                        <div className="col-12 col-md-4 px-1">
                          <div className="skeleton" style={{ width: "100%", height: "38px", borderRadius: "8px" }} />
                        </div>
                        <div className="col-12 col-md-4 px-1">
                          <div className="skeleton" style={{ width: "100%", height: "38px", borderRadius: "8px" }} />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-2 col-12  d-flex flex-column align-items-start">
                      <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                        Address <span className="error-text">*</span>
                      </div>
                      <div className="col-12">
                        <SearchWithFreeText
                          apiUrl={`${apiUrl}stops/to_address`}
                          placeholder="Search & type..."
                          selectedValue={toAddressOption}
                          onDropdownChange={(item) => {
                            setToAddressOption(item);
                            setToAddress(item.label);
                            setToCountryOption({
                              value: item.to_country_id ?? "",
                              label: item.to_country ?? "",
                            });
                            setToStateOption({
                              value: item.to_state_id ?? "",
                              label: item.to_state ?? "",
                            });
                            setToCityOption({
                              value: item.to_city_id ?? "",
                              label: item.to_city ?? "",
                            });
                            setToZipCode(item.to_zipcode ?? "");
                            setToLatitude(item.latitude ?? "");
                            setToLongitude(item.longitude ?? "");
                          }}
                          onChangeSearch={(value) => {
                            setToAddress(value);
                            setToAddressOption({ label: value, value: value });
                          }}
                        />
                        {/* <CommonDropdown
                        apiUrl={`${apiUrl}stops/to_address`}
                        placeholder="Select"
                        value={toAddressOption}
                        onSelect={(item) => {
                          setToAddressOption(item);
                          setToAddress(item.label);
                          setToCountryOption({
                            value: item.to_country_id ?? "",
                            label: item.to_country ?? "",
                          });
                          setToStateOption({
                            value: item.to_state_id ?? "",
                            label: item.to_state ?? "",
                          });
                          setToCityOption({
                            value: item.to_city_id ?? "",
                            label: item.to_city ?? "",
                          });
                          setToZipCode(item.to_zipcode ?? "");
                        }}
                      /> */}
                      </div>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#FF4444",
                          marginTop: "6px",
                          marginLeft: "6px",
                        }}
                      >
                        {errorMsg.to_address}
                      </span>
                    </div>

                    <div className="col-12 ">
                      <div className="row">
                        <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start">
                          <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                            City <span className="error-text">*</span>
                          </div>
                          <div className="w-100">
                            <CommonDropdown
                              apiUrl={`${adminUrl}get_city`}
                              placeholder="Select"
                              value={toCityOption}
                              onSelect={(item) => {
                                setToCityOption(item);
                                setToStateOption({
                                  value: item.state_id,
                                  label: item.state_name,
                                });
                                setToCountryOption({
                                  value: item.country_id,
                                  label: item.country_name,
                                });
                              }}
                            />
                          </div>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "start",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FF4444",
                              marginTop: "6px",
                              marginLeft: "6px",
                            }}
                          >
                            {errorMsg.to_city_id}
                          </span>
                        </div>
                        <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start  ps-0">
                          <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                            State <span className="error-text">*</span>
                          </div>
                          <div className="w-100">
                            <CommonDropdown
                              apiUrl={`${adminUrl}get_state`}
                              placeholder="Select"
                              value={toStateOption}
                              onSelect={(item) => {
                                setToStateOption(item);
                                setToCountryOption({
                                  value: item.country_id,
                                  label: item.country_name,
                                });
                              }}
                            />
                          </div>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "start",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FF4444",
                              marginTop: "6px",
                              marginLeft: "6px",
                            }}
                          >
                            {errorMsg.to_state_id}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 ">
                      <div className="row">
                        <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start">
                          <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                            Country <span className="error-text">*</span>
                          </div>
                          <div className="w-100">
                            <CommonDropdown
                              apiUrl={`${adminUrl}get_country`}
                              placeholder="Select"
                              value={toCountryOption}
                              onSelect={(item) => {
                                setToCountryOption(item);
                              }}
                            />
                          </div>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "start",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FF4444",
                              marginTop: "6px",
                              marginLeft: "6px",
                            }}
                          >
                            {errorMsg.to_country_id}
                          </span>
                        </div>

                        <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start ps-0">
                          <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                            Zip code <span className="error-text">*</span>
                          </div>
                          <div className="w-100">
                            <input
                              type="text"
                              className="form-control rounded-3 "
                              id="zipcode"
                              placeholder="Enter here"
                              value={toZipCode}
                              onChange={(e) => {
                                setToZipCode(e.target.value);
                              }}
                            />
                          </div>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "start",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FF4444",
                              marginTop: "6px",
                              marginLeft: "6px",
                            }}
                          >
                            {errorMsg.to_zipcode}
                          </span>
                        </div>
                        <div className="col-12 mb-3">
                          <div className="row px-2">
                            <div className="mt-2 col-12 col-md-4 px-1 ">
                              <input type="text" placeholder="Latitude" className="form-control rounded-3 fs-13" value={toLatitude} readOnly />
                              {errorMsg.to_latitude && <span className="error-text">{errorMsg.to_latitude}</span>}
                            </div>
                            <div className="mt-2 col-12 col-md-4 px-1 ">
                              <input type="text" placeholder="Longitude" className="form-control rounded-3 fs-13" value={toLongitude} readOnly />
                              {errorMsg.to_longitude && <span className="error-text">{errorMsg.to_longitude}</span>}
                            </div>
                            <div className="mt-2 col-12 col-md-4 px-1">
                              <Button
                                variant="primary"
                                className="w-100 fs-13 fw-500 d-flex align-items-center justify-content-center gap-1 map-confirm-btn"
                                onClick={() => {
                                  setSearchQuery("");
                                  setActiveField("addr2");
                                  setMapPosition(null);
                                  setShowMap(true);
                                }}
                              >
                                Pin in map
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="product-table-card">
            <div className="row">
              <div className="col-12 ">
                <div className="row">
                  <div className="col-xl-12 d-flex flex-column text-start">
                    <p className="fs-18 fw-600 mb-0">
                      Product <span className="text-danger">*</span>
                    </p>

                    {errorMsg.products && <span className="error-text mt-1">{errorMsg.products}</span>}
                  </div>

                  <div style={{ overflowY: "visible" }}>
                    <div className="mt-2 sticky-table-wrapper ">
                      <table className="product-table-create" border="1" cellPadding="10">
                        <thead>
                          <tr>
                            <th className="ps-2">PRODUCT NAME</th>
                            <th className="ps-2">WEIGHT</th>
                            <th className="ps-2">VOLUME</th>
                            <th className="ps-2">UNIT</th>
                            <th className="ps-2">QUANTITY</th>
                            <th className="ps-2">PRICE</th>
                            <th className="ps-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {isEditLoading
                            ? Array.from({ length: 3 }).map((_, idx) => (
                              <tr key={idx} style={{ height: "60px" }}>
                                {Array.from({ length: 7 }).map((_, colIdx) => (
                                  <td key={colIdx} className="px-2 py-1">
                                    <div className="skeleton skeleton-text" style={{ width: "90%", height: "13px" }} />
                                  </td>
                                ))}
                              </tr>
                            ))
                            : productList.map((row, index) => (
                              <tr
                                key={row.id}
                                style={{
                                  height: "60px",
                                  position: "relative",
                                  overflow: "visible",
                                }}
                              >
                                <td
                                  className="px-2 py-1"
                                  style={{
                                    position: "relative",
                                    overflow: "visible",
                                  }}
                                >
                                  <div className="pe-3 py-1" style={{ width: "200px" }}>
                                    {" "}
                                    <CommonDropdown
                                      apiUrl={`${apiUrl}item/autosuggestion`}
                                      placeholder="Select"
                                      value={row.product}
                                      onSelect={(item) => {
                                        productList[index]["product"] = item;
                                        setProductList(productList);
                                      }}
                                    />
                                    <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index, "product_id")}</span>
                                  </div>
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    className="form-control rounded-3 vendor-form-input"
                                    id={`weight` + index}
                                    placeholder="Enter here"
                                    value={row.weight}
                                    onChange={(e) => {
                                      const productTemp = [...productList];
                                      productTemp[index]["weight"] = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                                      setProductList(productTemp);
                                    }}
                                    style={{
                                      borderColor: getErrorMsg(index, "weight") !== "" ? "red" : "#EBEBEB",
                                    }}
                                  />{" "}
                                  <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index, "weight")}</span>
                                </td>

                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    className="form-control rounded-3 vendor-form-input"
                                    id={`volume` + index}
                                    placeholder="Enter here"
                                    value={row.volume}
                                    onChange={(e) => {
                                      const productTemp = [...productList];
                                      productTemp[index]["volume"] = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                                      setProductList(productTemp);
                                    }}
                                    style={{
                                      borderColor: getErrorMsg(index, "volume") !== "" ? "red" : "#EBEBEB",
                                    }}
                                  />{" "}
                                  <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index, "volume")}</span>
                                </td>

                                <td className="px-2 py-1" style={{ width: "150px" }}>
                                  <CommonDropdown
                                    apiUrl={`${apiUrl}stops/uoms?term=`}
                                    placeholder="Select"
                                    value={row.unit}
                                    onSelect={(item) => {
                                      productList[index]["unit"] = item;
                                      setProductList(productList);
                                    }}
                                  />
                                  <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index, "unit")}</span>
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control rounded-3 vendor-form-input"
                                    id={`quantity` + index}
                                    placeholder="Enter here"
                                    value={row.quantity}
                                    onChange={(e) => {
                                      const productTemp = [...productList];
                                      productTemp[index]["quantity"] = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                                      setProductList(productTemp);
                                    }}
                                    style={{
                                      borderColor: getErrorMsg(index, "quantity") !== "" ? "red" : "#EBEBEB",
                                    }}
                                  />{" "}
                                  <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index, "quantity")}</span>
                                </td>

                                <td>
                                  <div className="mx-2" style={{ minWidth: "124px" }}>
                                    <input
                                      type="text"
                                      className="form-control rounded-3 vendor-form-input"
                                      id={`Price` + index}
                                      placeholder="Enter here"
                                      value={row.price}
                                      onChange={(e) => {
                                        const productTemp = [...productList];
                                        productTemp[index]["price"] = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                                        setProductList(productTemp);
                                      }}
                                      style={{
                                        borderColor: getErrorMsg(index, "price") !== "" ? "red" : "#EBEBEB",
                                      }}
                                    />
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      color: "#FF4444",
                                    }}
                                  >
                                    {getErrorMsg(index, "price")}
                                  </span>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      cursor: "pointer",
                                      marginTop: "14px",
                                    }}
                                    onClick={() => {
                                      if (row.id !== null && row.id !== "") {
                                        setRemovedProductList([...removedProductList, row.id]);
                                      }
                                      const productListTemp = [...productList];
                                      productListTemp.splice(index, 1);
                                      setProductList(productListTemp);
                                    }}
                                    className="mx-2 ps-2 d-flex align-items-center justify-content-center "
                                  >
                                    <img src={deleteIcon} alt="delete" />
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-xl-12 mt-2">
                    <Button
                      className={`cursor-pointer border-0 rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
                      onClick={() => {
                        setProductList([
                          ...productList,
                          {
                            id: 0,
                            product: null,
                            weight: 0,
                            volume: 0,
                            unit: null,
                            quantity: 0,
                            price: 0,
                          },
                        ]);
                      }}
                      style={{
                        minWidth: "100px",
                        background: "#E6E4FF",
                        color: "#212129",
                        fontWeight: "600",
                        fontSize: "13px",
                      }}
                    >
                      Add product
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showMap} onHide={() => setShowMap(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Location</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <div className="d-flex gap-2">
              <div style={{ position: "relative", flex: 1 }}>
                <Form.Control
                  type="text"
                  placeholder="Search for a location (e.g., Mumbai, India or specific address)"
                  className="fs-13"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />

                {searchResults.length > 0 && (
                  <div className="search-results-dropdown">
                    {searchResults.map((result, index) => (
                      <div
                        key={index}
                        className="search-result-item"
                        onClick={() => handleSelectSearchResult(result)}
                      >
                        <div className="fs-13 fw-500">{result.display_name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="primary" onClick={handleSearch} disabled={isSearching || !searchQuery.trim()} style={{ backgroundColor: "#3469E7", borderColor: "#3469E7" }}>
                {isSearching ? "Searching..." : "Search"}
              </Button>
            </div>
          </div>
          <div style={{ height: "400px", width: "100%" }}>
            <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
              <LocationPicker setPosition={setMapPosition} />
              {mapPosition && <Marker position={mapPosition} />}
            </MapContainer>
          </div>
          <div className="mt-2 text-muted fs-13">{mapPosition ? `Selected: ${mapPosition.lat.toFixed(6)}, ${mapPosition.lng.toFixed(6)}` : "Click on the map to select a location"}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowMap(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleMapConfirm} disabled={!mapPosition} style={{ backgroundColor: "#3469E7", borderColor: "#3469E7" }}>
            Select Location
          </Button>
        </Modal.Footer>
      </Modal>

      <CommonSnackbar
        show={showSnackbar}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        isSuccess={snackbarStatus}
      />
    </div>
  );
};
